import { logger } from './LogUtil'
import { EnvUtil } from './EnvUtil'
import { HtmlUtil } from './HtmlUtil'
import { UnitUtil } from './UnitUtil'
import { AppUtil, AppUtilEvent } from './AppUtil'
import { onLCP, onFID, onCLS, onINP, onFCP, onTTFB, Metric } from 'web-vitals'

import Vue, { Component } from 'vue'
import VueRouter, { RawLocation } from 'vue-router'

let _setRemFunc: (..._args: any[]) => any
let _setRemFuncPS: (..._args: any[]) => any
let _targetRootFontSize = 12

// import * as Sentry from '@sentry/vue'
// import { Integrations } from '@sentry/tracing'
// import { init as initApmRum } from '@elastic/apm-rum'

/**
 * vue 系统的util方法, 系统级的方法处理
 */
export class VueUtil {
  static _vconsole: any = null
  static _hasVConsole = false

  /**
   * 七鱼客服系统
   */
  public static addYsf() {
    HtmlUtil.appendScript('https://qiyukf.com/script/c5ca1c320f435755744f93689d4ea0f6.js', () => {
      // do nothing
    })
  }

  /**
   * 打开七鱼默认对话框
   */
  public static openYsf() {
    ;(window as any).ysf('open')
  }

  /**
   * 加上console
   */
  public static addVConsole(): boolean {
    if (EnvUtil.isDev()) {
      return false
    }

    // 尽量快速点击屏幕左上角, 3秒内超过8次就打开/关闭console
    let clickCache: { [key: string]: number } = {}

    document.addEventListener('click', (evt: MouseEvent) => {
      if (evt.clientX < 100 && evt.clientY < 30) {
        const ts = `${Math.ceil(new Date().getTime() / 3000)}`
        if (!clickCache[ts]) {
          clickCache[ts] = 0
        }
        clickCache[ts] += 1

        if (clickCache[ts] > 8) {
          if (this._hasVConsole) {
            this.doRemoveVConsole()
          } else {
            this.doAddVConsole()
          }

          clickCache[ts] = 0
        }

        // 10s钟后清空数据
        setTimeout(() => {
          clickCache = {}
        }, 30000)
      }
    })

    if (EnvUtil.isProd()) {
      return false
    } else {
      this.doAddVConsole()
      return true
    }
  }

  protected static doAddVConsole() {
    if (!this._hasVConsole) {
      this._hasVConsole = true
      HtmlUtil.appendScript('https://cdn.tiantiantiaosheng.com/site/assets/commons/vconsole-3.2.0.min.js', () => {
        // eslint-disable-next-line
        this._vconsole = new (window as any).VConsole()
      })
    }
  }

  protected static doRemoveVConsole() {
    if (this._hasVConsole) {
      this._vconsole?.destroy()
      this._vconsole = null
      this._hasVConsole = false
      document
        .querySelector('script[src="https://cdn.tiantiantiaosheng.com/site/assets/commons/vconsole-3.2.0.min.js"]')
        ?.remove()
      document.querySelector('#__vconsole')?.remove()
    }
  }

  public static setRem<T extends (...args: any[]) => any>(_func: T, ..._args: Parameters<T>) {
    AppUtil.remove(AppUtilEvent.Resize, _setRemFunc)
    window.removeEventListener('resize', _setRemFunc)
    window.removeEventListener('pageshow', _setRemFuncPS)

    _setRemFunc = () => _func(..._args)
    _setRemFunc()
    _setRemFuncPS = (e) => e.persisted && _func(..._args)

    // fix rem on iphone plus8
    AppUtil.on(AppUtilEvent.Resize, _setRemFunc)
    window.addEventListener('resize', _setRemFunc)
    window.addEventListener('pageshow', _setRemFuncPS)
  }

  public static _setRem(actualWidth: number, targetWidth = 375, targetRootFontSize = 12.0) {
    _targetRootFontSize = targetRootFontSize

    document.documentElement.style.fontSize = (actualWidth * targetRootFontSize) / targetWidth + 'px'
  }

  /**
   * 设定一个标准的rem的px
   * @param targetWidth 目标屏幕尺寸大小 默认为 375px
   * @param baseFontSize 标准字体大小， 默认为 targetWidth下的 12px
   */
  public static setRemByRoot(targetWidth = 375, baseFontSize = 12.0) {
    return this.setRemByMainEleId(undefined, targetWidth, baseFontSize)
  }

  public static setRemByMainEleId(mainEleId = 'app', targetWidth = 375, baseFontSize = 12.0) {
    // 设定一个1rem的宽度，所有的内容都以rem为标准
    const deviceWidth = this.getActualClientWidth(mainEleId)

    this._setRem(deviceWidth, targetWidth, baseFontSize)
  }

  /**
   * Only do this for ipad or like devices.
   *
   * @param mainEleId
   * @param targetAspectRatio
   * @param targetWidth
   * @param baseFontSize
   */
  public static setRemFixedAspectRatio(
    mainEleId = '',
    targetAspectRatio = 375 / 812,
    targetWidth = 375,
    baseFontSize = 12.0
  ) {
    const deviceWidth = this.getActualClientWidth(mainEleId)
    const deviceHeight = this.getActualClientHeight(mainEleId)

    let actualWidth = deviceWidth

    // Assume that the aspect ratio of the device is greater than 9/16 is ipad or like device.
    // https://uiiiuiii.com/screen/
    // 9 / 16 adjust a little to 0.58 for iphone8plus
    if (deviceWidth / deviceHeight > 0.58) {
      actualWidth = Math.min(deviceWidth, Math.max(targetWidth, deviceHeight * targetAspectRatio))
    }

    this._setRem(actualWidth, targetWidth, baseFontSize)
  }

  public static getActualPx(px: number, targetRootFontSize = _targetRootFontSize) {
    const rootFontSize = parseFloat(document.documentElement.style.fontSize) || targetRootFontSize

    return (rootFontSize / targetRootFontSize) * px
  }

  public static getActualClientWidth(mainEleId = 'app') {
    let deviceWidth = document.documentElement.clientWidth

    if (mainEleId) {
      const ele = document.getElementById(mainEleId)
      if (ele) {
        deviceWidth = ele.clientWidth
      }
    }

    return deviceWidth
  }

  public static getActualClientHeight(mainEleId = 'app') {
    let deviceHeight = document.documentElement.clientHeight

    if (mainEleId) {
      const ele = document.getElementById(mainEleId)
      if (ele) {
        deviceHeight = ele.clientHeight
      }
    }

    return deviceHeight
  }

  /**
   * 集成APM
   * @param serviceName
   */
  public static addAPM(/* serviceName: string */) {
    // Optimize build, remove APM.
    // if (EnvUtil.isProd()) {
    //   initApmRum({
    //     // Set required service name (allowed characters: a-z, A-Z, 0-9, -, _, and space)
    //     serviceName: serviceName,
    //     // Set custom APM Server URL (default: http://localhost:8200)
    //     serverUrl: 'https://apm-server.tiantiantiaosheng.com',
    //     // Set service version (required for sourcemap feature)
    //     serviceVersion: '1.0.0',
    //   })
    // }
  }

  /**
   * 集成Sentry
   */
  public static addSentry(/* dsn: string */) {
    // Optimize build, remove Sentry.
    // if (EnvUtil.isProd() || EnvUtil.isStaging()) {
    //   Sentry.init({
    //     Vue,
    //     dsn,
    //     logErrors: true,
    //     environment: process.env.VUE_APP_ENV,
    //     integrations: [new Integrations.BrowserTracing()],
    //     tracesSampleRate: 0.002,
    //   })
    // }
  }

  /**
   * 类似router.push,但直接跳转到新的tab
   * @param router vuerouter
   * @param rawLocation vue router location参数
   */
  public static route2BlankTab(router: VueRouter, rawLocation: RawLocation) {
    const routeData = router.resolve(rawLocation)
    window.open(routeData.href, '_blank')
  }

  /**
   * 将错误报告到ELK
   */
  public static reportErrorToELK() {
    // noop
  }

  public static visibleSyncWrapper(
    _component: Component | (() => Component) | string,
    _props?: Record<string, any>,
    _on?: Record<string, Function | Function[]>
  ) {
    const instance = new Vue({
      el: document.createElement('div'),
      data: () => ({ visible: true }),
      render: function (_h) {
        return this.visible
          ? _h(_component, {
              on: {
                ...(_on || {}),
                'update:visible': (_value: boolean) => {
                  this.visible = _value
                },
              },
              props: { ...(_props || {}) },
              attrs: { visible: true },
            })
          : null
      },
      methods: {
        close() {
          this.visible = false
        },
      },
    })

    ;(document.querySelector('#app') || document.body).appendChild(instance.$el)

    return instance as typeof instance & { close: () => void }
  }

  public static async setARMSConfig(config: { uid?: string; environment?: string; release?: string }) {
    const __bl = (window as any).__bl
    if (__bl) {
      __bl.setConfig(config)
      ;[onLCP, onFID, onCLS, onINP, onFCP, onTTFB].forEach((f) =>
        f((e: Metric) => {
          __bl.avg(e.name, UnitUtil.toFixed(e.value, 2))

          if (f === onCLS && e.value >= 0.1) {
            logger.event('Performance', 'CWV', f.name, e.value, e)
          }
        })
      )
    }
  }

  public static async ensureImagesAreLoaded(vm: Vue) {
    await vm.$nextTick()

    const imageUrls = [...vm.$el.querySelectorAll('img')].map(
      (image: HTMLImageElement) => image.src || image.dataset.src
    )

    const promises = imageUrls.filter(Boolean).map((url) => {
      return new Promise((resolve) => {
        const image = new Image()

        image.onload = resolve
        image.onerror = resolve
        image.src = url as string
        image.crossOrigin = 'anonymous'

        return image
      })
    })

    return promises
  }
}
